import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import Select from 'react-select';

import { datamodelItemsQuery } from '@queries';
import { ButtonSpinnerDarkIcon } from '@svgs';

const formatDatamodelItemOptions = (defaultOptions, datamodelItemsData) => {
  let objects = defaultOptions;
  if (datamodelItemsData) {
    // combine the lists
    //
    // exclude items already on the default list
    const newObjects = datamodelItemsData.objects.filter(
      (item) => !objects.find((i) => i.id === item.id),
    );
    objects = [...objects, ...newObjects];
  }
  return objects.map((datamodelItem) => {
    return { value: datamodelItem.id, label: datamodelItem.name };
  });
};

export const DatamodelItemFieldField = ({
  field,
  datamodelID,
  defaultOptions = [],
}) => {
  // based on:
  // https://github.com/kkomelin/react-select-customization-examples/blob/main/src/components/ExampleRemoteDataDebouncingAndCaching.tsx
  const [inputText, setInputText] = useState('');
  const [searchText, setSearchText] = useState('');

  // get datamodelItems via live search - also runs with an empty q on mount
  // so opening the dropdown shows a default list of options rather than
  // nothing until the user types
  const { data: datamodelItemsData, isPending: datamodelItemsIsPending } =
    useQuery(
      datamodelItemsQuery({
        datamodel_id: datamodelID,
        limit: 10,
        order_by: 'name',
        q: searchText,
      }),
    );

  // combine and format the options for the field
  const combinedOptions = formatDatamodelItemOptions(
    defaultOptions,
    datamodelItemsData,
  );

  const handleSearchDebounced = useRef(
    debounce((searchText) => setSearchText(searchText), 300),
  ).current;

  const noOptionsMessage = (obj) => {
    if (obj.inputValue.trim().length === 0) {
      return null;
    }
    return 'No matching content database items';
  };

  const handleInputChange = (inputText, meta) => {
    if (meta.action !== 'input-blur' && meta.action !== 'menu-close') {
      setInputText(inputText);
      handleSearchDebounced(inputText);
    }
  };

  return (
    <Select
      name={field.name}
      inputRef={field.ref}
      options={combinedOptions}
      isClearable={true}
      className="basic-multi-select"
      classNamePrefix="select"
      isLoading={datamodelItemsIsPending}
      inputValue={inputText}
      onInputChange={handleInputChange}
      noOptionsMessage={noOptionsMessage}
      onChange={(val) => {
        console.log('onChange', val);
        return field.onChange(val ? val.value : null);
      }}
      // defaultValue needs to be the choice object with `value` and `label` properties
      defaultValue={
        field.value
          ? combinedOptions.find((c) => c.value === field.value)
          : null
      }
    />
  );
};

// wrapper around the field so we dont render the field
// until we try to load all the items first
//
// might be able to refactor in future to remove this requirement
export const DatamodelItemField = ({
  field,
  datamodelID,
  defaultDatamodelItemID,
}) => {
  // get datamodelItems that are already selected
  const {
    data: existingDatamodelItemData,
    isPending: existingDatamodelItemIsPending,
  } = useQuery(
    datamodelItemsQuery(
      {
        datamodel_id: datamodelID,
        limit: 10,
        order_by: 'name',
        id: defaultDatamodelItemID,
      },
      {
        enabled: !!defaultDatamodelItemID,
      },
    ),
  );

  // show loading
  const isLoading = existingDatamodelItemIsPending && defaultDatamodelItemID;

  return (
    <>
      {isLoading && <ButtonSpinnerDarkIcon />}
      {!isLoading && (
        <DatamodelItemFieldField
          field={field}
          datamodelID={datamodelID}
          defaultOptions={
            existingDatamodelItemData ? existingDatamodelItemData.objects : []
          }
        />
      )}
    </>
  );
};
